import React, { useState, useEffect, useMemo } from "react";
import { api, UserRecord, UserRole, BusinessUnit, CostCenter } from "../api";

const ROLES: { value: UserRole; label: string }[] = [
  { value: "cc_owner", label: "CC Owner" },
  { value: "cc_leader", label: "CC Leader" },
  { value: "bu_leader", label: "BU Leader" },
  { value: "admin", label: "Admin" },
];

const ROLE_CLASS: Record<string, string> = {
  admin:     "admin-role--admin",
  bu_leader: "admin-role--bu-leader",
  cc_leader: "admin-role--cc-leader",
  cc_owner:  "admin-role--cc-owner",
};

/** Parse "3304,3307,3309" → ["3304","3307","3309"] */
function parseCCs(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(",").map(s => s.trim()).filter(Boolean);
}

/** Parse "1:Central - CORP,2:Technology" → [{id:1,name:"Central - CORP"}, ...] */
function parseBUs(raw: string | null): { id: number; name: string }[] {
  if (!raw) return [];
  return raw.split(",").map(s => {
    const [idStr, ...nameParts] = s.split(":");
    return { id: parseInt(idStr, 10), name: nameParts.join(":") };
  }).filter(b => !isNaN(b.id));
}

interface EditFormState {
  name: string;
  email: string;
  role: UserRole;
  cost_centers: string[];
  manager_id: number | null;
  business_units: number[];
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);

  // Add user form
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState<EditFormState>({
    name: "", email: "", role: "cc_owner",
    cost_centers: [], manager_id: null, business_units: [],
  });
  const [addError, setAddError] = useState("");

  // Search
  const [search, setSearch] = useState("");

  // BU filter for CC chip grids in forms
  const [addFormBUFilter, setAddFormBUFilter] = useState<string>("");
  const [editFormBUFilter, setEditFormBUFilter] = useState<string>("");

  // Password reset modal state
  const [resetConfirmUser, setResetConfirmUser] = useState<{ id: number; name: string } | null>(null);
  const [resetResult, setResetResult] = useState<{ name: string; tempPassword: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // Edit state (expand panel)
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>({
    name: "", email: "", role: "cc_owner",
    cost_centers: [], manager_id: null, business_units: [],
  });
  const [editError, setEditError] = useState("");

  useEffect(() => { loadAll(); }, []);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/users"),
      api.get("/cost_centers"),
      api.get("/business-units"),
    ]).then(([uRes, ccRes, buRes]) => {
      setUsers(uRes.data);
      setCostCenters(ccRes.data);
      setBusinessUnits(buRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  // Map CC number → BU name for deriving user BUs from their CCs
  const ccToBU = useMemo(() => {
    const map: Record<string, string> = {};
    costCenters.forEach(cc => {
      if (cc.business_unit) map[cc.cost_center_number] = cc.business_unit;
    });
    return map;
  }, [costCenters]);

  // Unique BU names for BU filter dropdown in forms
  const buNames = useMemo(() => {
    const names = new Set<string>();
    costCenters.forEach(cc => { if (cc.business_unit) names.add(cc.business_unit); });
    return Array.from(names).sort();
  }, [costCenters]);

  // Derive a user's BU(s) from their owned cost centers
  const getUserBUs = (userCCs: string[]): string[] => {
    const bus = new Set<string>();
    userCCs.forEach(ccNum => { if (ccToBU[ccNum]) bus.add(ccToBU[ccNum]); });
    return Array.from(bus);
  };

  // Search filter
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase().trim();
    return users.filter(u => {
      const nameMatch = u.name.toLowerCase().includes(q);
      const ccMatch = parseCCs(u.owned_ccs).some(cc => cc.includes(q));
      const roleLabel = ROLES.find(r => r.value === u.role)?.label || u.role;
      const roleMatch = roleLabel.toLowerCase().includes(q);
      const buMatch = getUserBUs(parseCCs(u.owned_ccs)).some(bu => bu.toLowerCase().includes(q));
      return nameMatch || ccMatch || roleMatch || buMatch;
    });
  }, [users, search, ccToBU]);

  const handleAdd = async () => {
    if (!addForm.email || !addForm.name) { setAddError("Email and name required"); return; }
    try {
      await api.post("/users", addForm);
      setShowAdd(false);
      setAddForm({ name: "", email: "", role: "cc_owner", cost_centers: [], manager_id: null, business_units: [] });
      setAddError("");
      loadAll();
    } catch (err: any) {
      setAddError(err.response?.data?.error || "Failed to create user");
    }
  };

  const openEdit = (u: UserRecord) => {
    setEditingId(u.id);
    setEditError("");
    setEditFormBUFilter("");
    setEditForm({
      name: u.name,
      email: u.email,
      role: u.role,
      cost_centers: parseCCs(u.owned_ccs),
      manager_id: u.manager_id,
      business_units: parseBUs(u.assigned_bus).map(b => b.id),
    });
  };

  const handleSaveEdit = async (id: number) => {
    try {
      await api.put(`/users/${id}`, editForm);
      setEditingId(null);
      setEditError("");
      loadAll();
    } catch (err: any) {
      setEditError(err.response?.data?.error || "Failed to update");
    }
  };

  const handleResetPwd = async (id: number, name: string) => {
    setResetConfirmUser({ id, name });
  };

  const confirmResetPwd = async () => {
    if (!resetConfirmUser) return;
    try {
      const res = await api.post(`/users/${resetConfirmUser.id}/reset-password`);
      setResetResult({ name: resetConfirmUser.name, tempPassword: res.data.tempPassword });
      setResetConfirmUser(null);
      setCopied(false);
      loadAll();
    } catch {
      setResetConfirmUser(null);
    }
  };

  const handleCopyPassword = () => {
    if (!resetResult) return;
    navigator.clipboard.writeText(resetResult.tempPassword);
    setCopied(true);
  };

  const handleDeactivate = async (id: number) => {
    if (!window.confirm("Deactivate this user?")) return;
    await api.delete(`/users/${id}`);
    loadAll();
  };

  const handleActivate = async (id: number) => {
    await api.put(`/users/${id}`, { is_active: true });
    loadAll();
  };

  // Toggle CC in a form state's cost_centers array
  const toggleCC = (form: EditFormState, setForm: React.Dispatch<React.SetStateAction<EditFormState>>, ccNum: string) => {
    setForm(f => ({
      ...f,
      cost_centers: f.cost_centers.includes(ccNum)
        ? f.cost_centers.filter(c => c !== ccNum)
        : [...f.cost_centers, ccNum],
    }));
  };

  // Toggle BU in a form state's business_units array
  const toggleBU = (form: EditFormState, setForm: React.Dispatch<React.SetStateAction<EditFormState>>, buId: number) => {
    setForm(f => ({
      ...f,
      business_units: f.business_units.includes(buId)
        ? f.business_units.filter(b => b !== buId)
        : [...f.business_units, buId],
    }));
  };

  /** Render CC/BU/Manager assignment section (reused for add + edit) */
  const renderAssignmentFields = (
    form: EditFormState,
    setForm: React.Dispatch<React.SetStateAction<EditFormState>>,
    userId?: number,
  ) => {
    const showBU = form.role === "bu_leader";
    const showManager = form.role !== "admin";
    const isEditForm = userId !== undefined;
    const activeBUFilter = isEditForm ? editFormBUFilter : addFormBUFilter;
    const setActiveBUFilter = isEditForm ? setEditFormBUFilter : setAddFormBUFilter;

    // Filter CCs by selected BU (if any)
    const visibleCCs = costCenters.filter(cc => {
      if (cc.cost_center_number === "0000") return false;
      if (activeBUFilter && cc.business_unit !== activeBUFilter) return false;
      return true;
    });

    return (
      <div className="admin-edit-assignments">
        {/* Cost Centers */}
        <div className="admin-edit-field">
          <label className="admin-edit-label">
            <span className="material-icons au-label-icon">account_balance</span>
            Owned Cost Centers
          </label>
          <div className="au-cc-filter-row">
            <select
              className="admin-select au-bu-filter-select"
              value={activeBUFilter}
              onChange={e => setActiveBUFilter(e.target.value)}
            >
              <option value="">All Business Units</option>
              {buNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            {activeBUFilter && (
              <button
                type="button"
                className="admin-btn admin-btn--ghost au-bu-filter-clear"
                onClick={() => setActiveBUFilter("")}
              >
                <span className="material-icons">close</span>
              </button>
            )}
          </div>
          <div className="admin-chip-grid">
            {visibleCCs.map(cc => {
              const selected = form.cost_centers.includes(cc.cost_center_number);
              return (
                <button
                  key={cc.cost_center_number}
                  type="button"
                  className={`admin-chip-toggle ${selected ? "admin-chip-toggle--on" : ""}`}
                  onClick={() => toggleCC(form, setForm, cc.cost_center_number)}
                >
                  <span className="admin-chip-toggle-check">{selected ? "\u2713" : ""}</span>
                  {cc.cost_center_number}
                  <span className="admin-chip-toggle-name">{cc.cost_center_name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Manager */}
        {showManager && (
          <div className="admin-edit-field">
            <label className="admin-edit-label">
              <span className="material-icons au-label-icon">supervisor_account</span>
              Reports To
            </label>
            <select
              className="admin-select"
              value={form.manager_id ?? ""}
              onChange={e => setForm(f => ({ ...f, manager_id: e.target.value ? parseInt(e.target.value, 10) : null }))}
            >
              <option value="">No manager</option>
              {users
                .filter(u => u.id !== userId && u.is_active)
                .map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({ROLES.find(r => r.value === u.role)?.label})
                  </option>
                ))
              }
            </select>
          </div>
        )}

        {/* Business Units (only for bu_leader / vp) */}
        {showBU && (
          <div className="admin-edit-field">
            <label className="admin-edit-label">
              <span className="material-icons au-label-icon">business</span>
              Business Unit Oversight
            </label>
            <div className="admin-chip-grid">
              {businessUnits.map(bu => {
                const selected = form.business_units.includes(bu.id);
                return (
                  <button
                    key={bu.id}
                    type="button"
                    className={`admin-chip-toggle ${selected ? "admin-chip-toggle--on" : ""}`}
                    onClick={() => toggleBU(form, setForm, bu.id)}
                  >
                    <span className="admin-chip-toggle-check">{selected ? "\u2713" : ""}</span>
                    {bu.code}
                    <span className="admin-chip-toggle-name">{bu.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="au-loading">Loading users...</div>;

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <span className="material-icons">people</span>
        <span className="admin-section-title">Users</span>
        <span className="admin-section-subtitle">{users.length} User{users.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="admin-section-body">
        {/* Toolbar */}
        <div className="admin-cc-toolbar">
          <button className="admin-btn admin-btn--primary" onClick={() => { setShowAdd(!showAdd); if (showAdd) setAddError(""); }}>
            <span className="material-icons">{showAdd ? "close" : "person_add"}</span>
            {showAdd ? "Cancel" : "Add User"}
          </button>
          <div className="au-search-wrapper">
            <span className="material-icons au-search-icon">search</span>
            <input
              className="admin-text-input au-search-input"
              placeholder="Search name, cost center, role..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="au-search-clear"
                onClick={() => setSearch("")}
              >
                <span className="material-icons">close</span>
              </button>
            )}
          </div>
        </div>

        {/* Add Form */}
        {showAdd && (
          <div className="admin-edit-panel">
            <div className="admin-edit-panel-title">New User</div>
            <div className="admin-edit-row">
              <div className="admin-edit-field">
                <label className="admin-edit-label">Name</label>
                <input className="admin-text-input" placeholder="Full Name" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="admin-edit-field">
                <label className="admin-edit-label">Email</label>
                <input className="admin-text-input" placeholder="user@company.com" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="admin-edit-field">
                <label className="admin-edit-label">Role</label>
                <select className="admin-select" value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value as UserRole, business_units: [] }))}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            {renderAssignmentFields(addForm, setAddForm)}
            <div className="admin-edit-actions">
              <button className="admin-btn admin-btn--success" onClick={handleAdd}>
                <span className="material-icons">check</span> Create User
              </button>
              <button className="admin-btn admin-btn--ghost" onClick={() => { setShowAdd(false); setAddError(""); }}>Cancel</button>
              {addError && <span className="admin-toast admin-toast--err">{addError}</span>}
            </div>
          </div>
        )}

        {/* Users Table */}
        <table className="admin-cc-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Business Unit</th>
              <th>Cost Centers</th>
              <th>Manager</th>
              <th>Status</th>
              <th className="au-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => {
              const userCCs = parseCCs(u.owned_ccs);
              const userBUs = parseBUs(u.assigned_bus);
              const derivedBUs = getUserBUs(userCCs);

              return (
                <React.Fragment key={u.id}>
                  <tr>
                    <td className="au-td-name">{u.name}</td>
                    <td className="au-td-email">{u.email}</td>
                    <td>
                      <span className={`admin-role-badge ${ROLE_CLASS[u.role] || ""}`}>
                        {ROLES.find(r => r.value === u.role)?.label || u.role}
                      </span>
                    </td>
                    <td className="au-td-bu">
                      {derivedBUs.length > 0
                        ? derivedBUs.join(", ")
                        : "\u2014"
                      }
                    </td>
                    <td className="au-td-ccs">
                      {userCCs.length > 0
                        ? userCCs.join(", ")
                        : "\u2014"
                      }
                    </td>
                    <td className={u.manager_name ? "au-td-manager" : "au-td-manager au-td-manager--empty"}>
                      {u.manager_name || "\u2014"}
                    </td>
                    <td>
                      <span className={`au-status ${u.is_active ? "au-status--active" : "au-status--inactive"}`}>
                        {u.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="au-td-actions">
                      <div className="admin-btn-row au-btn-row">
                        <button
                          className={`admin-btn ${editingId === u.id ? "admin-btn--primary" : "admin-btn--ghost"}`}
                          onClick={() => editingId === u.id ? setEditingId(null) : openEdit(u)}
                        >
                          <span className="material-icons">{editingId === u.id ? "close" : "edit"}</span>
                          {editingId === u.id ? "Close" : "Edit"}
                        </button>
                        <button className="admin-btn admin-btn--warn" onClick={() => handleResetPwd(u.id, u.name)}>
                          <span className="material-icons">lock_reset</span>
                        </button>
                        {u.is_active ? (
                          <button className="admin-btn admin-btn--danger" onClick={() => handleDeactivate(u.id)}>
                            <span className="material-icons">person_off</span>
                          </button>
                        ) : (
                          <button className="admin-btn admin-btn--success" onClick={() => handleActivate(u.id)}>
                            <span className="material-icons">person</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Edit panel */}
                  {editingId === u.id && (
                    <tr>
                      <td colSpan={8} className="admin-edit-panel-cell">
                        <div className="admin-edit-panel">
                          <div className="admin-edit-panel-title">
                            Editing {u.name}
                            {userBUs.length > 0 && (
                              <span className="admin-edit-panel-subtitle">
                                BU: {userBUs.map(b => b.name).join(", ")}
                              </span>
                            )}
                          </div>
                          <div className="admin-edit-row">
                            <div className="admin-edit-field">
                              <label className="admin-edit-label">Name</label>
                              <input className="admin-text-input" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                            </div>
                            <div className="admin-edit-field">
                              <label className="admin-edit-label">Email</label>
                              <input className="admin-text-input" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                            </div>
                            <div className="admin-edit-field">
                              <label className="admin-edit-label">Role</label>
                              <select className="admin-select" value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value as UserRole, business_units: e.target.value !== "bu_leader" ? [] : f.business_units }))}>
                                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                              </select>
                            </div>
                          </div>
                          {renderAssignmentFields(editForm, setEditForm, u.id)}
                          <div className="admin-edit-actions">
                            <button className="admin-btn admin-btn--success" onClick={() => handleSaveEdit(u.id)}>
                              <span className="material-icons">check</span> Save
                            </button>
                            <button className="admin-btn admin-btn--ghost" onClick={() => setEditingId(null)}>Cancel</button>
                            {editError && <span className="admin-toast admin-toast--err">{editError}</span>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirm Reset Modal */}
      {resetConfirmUser && (
        <div className="au-modal-overlay" onClick={() => setResetConfirmUser(null)}>
          <div className="au-modal" onClick={e => e.stopPropagation()}>
            <div className="au-modal-icon au-modal-icon--warn">
              <span className="material-icons">lock_reset</span>
            </div>
            <h3 className="au-modal-title">Reset Password</h3>
            <p className="au-modal-text">
              Generate a new temporary password for <strong>{resetConfirmUser.name}</strong>?
              They will be required to change it on next login.
            </p>
            <div className="au-modal-actions">
              <button className="admin-btn admin-btn--ghost" onClick={() => setResetConfirmUser(null)}>Cancel</button>
              <button className="admin-btn admin-btn--warn" onClick={confirmResetPwd}>
                <span className="material-icons">lock_reset</span> Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Temp Password Result Modal */}
      {resetResult && (
        <div className="au-modal-overlay" onClick={() => setResetResult(null)}>
          <div className="au-modal" onClick={e => e.stopPropagation()}>
            <div className="au-modal-icon au-modal-icon--success">
              <span className="material-icons">check_circle</span>
            </div>
            <h3 className="au-modal-title">Password Reset</h3>
            <p className="au-modal-text">
              Temporary password for <strong>{resetResult.name}</strong>:
            </p>
            <div className="au-temp-pwd-box">
              <code className="au-temp-pwd-code">{resetResult.tempPassword}</code>
              <button className="au-temp-pwd-copy" onClick={handleCopyPassword} title="Copy to clipboard">
                <span className="material-icons">{copied ? "check" : "content_copy"}</span>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="au-modal-hint">
              Send this password to the user. They will be prompted to set a new password on first login.
            </p>
            <div className="au-modal-actions">
              <button className="admin-btn admin-btn--primary" onClick={() => setResetResult(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
